import * as svgUtils from '@sosocio/frontend-utils/svg';
import loadImage from 'services/load-image';
import { isSVGBackgroundPatternSource } from 'tools/background-pattern';

const svgBackgroundPatternPromises = new Map<string, Promise<HTMLImageElement>>();

/**
 * Normalizes and deduplicates an in-flight SVG pattern load across pages that
 * use the same source. Failed metadata reads fall back to the previous image
 * loader.
 *
 * @param source Resolved SVG background pattern source.
 * @returns Loaded SVG image with stable intrinsic dimensions when available.
 */
function loadSVGBackgroundPattern(source: string): Promise<HTMLImageElement> {
	const pendingImage = svgBackgroundPatternPromises.get(source);

	if (pendingImage) {
		return pendingImage;
	}

	const imagePromise = svgUtils
		.load(source)
		.then(({ image }) => image)
		.catch(async () => {
			const { image } = await loadImage(source);

			return image;
		});

	svgBackgroundPatternPromises.set(
		source,
		imagePromise,
	);
	imagePromise
		.catch(() => {
			/**
			 * Do nothing: swallow error
			 */
		})
		.finally(() => {
			svgBackgroundPatternPromises.delete(source);
		});

	return imagePromise;
}

/**
 * Loads one background pattern tile with stable intrinsic dimensions.
 *
 * SVGs use their explicit root dimensions, falling back to their `viewBox`;
 * raster images retain the intrinsic dimensions returned by the image loader.
 * If SVG metadata cannot be read, the standard loader preserves the previous
 * best-effort rendering behavior.
 *
 * @param source Resolved background pattern source.
 * @returns Loaded image used to create the repeating canvas pattern.
 */
export default function loadBackgroundPattern(source: string): Promise<HTMLImageElement> {
	if (isSVGBackgroundPatternSource(source)) {
		return loadSVGBackgroundPattern(source);
	}

	return loadImage(source)
		.then(({ image }) => image);
}
